import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Modal, Result } from 'antd';
import { EyeOutlined, LockOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import { TOOL_PAYMENT_ENABLED } from '../../../constants/payment.js';
import { SUPPORT_HOTLINE_DISPLAY, SUPPORT_ZALO_URL } from '../../../constants/supportBotData.js';

const formatVnd = (price) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(price);

/**
 * Lời mời + các nút mua công cụ trả phí, dùng chung cho màn khóa (ToolAccessGuard) và modal mua (ToolPurchaseModal).
 * Chưa đăng nhập: nút đăng nhập / tạo tài khoản. Đã đăng nhập: nút mua từng gói -> /checkout/:planId?returnTo=<trang hiện tại>.
 * Tắt bán online (TOOL_PAYMENT_ENABLED = false) hoặc không có gói có giá: mời nhắn Zalo.
 * @param {Array} allPlans requiredPlans của công cụ (GET /pharmacy-tool/access)
 * @param {Function} [onNavigate] gọi trước khi chuyển trang (VD đóng modal)
 */
export function useToolPurchaseOptions(allPlans = [], onNavigate) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
  // Gói 0đ (VD Đối Tác) không thanh toán online được — chỉ hiện nút mua cho gói có giá
  const plans = allPlans.filter((p) => p.price > 0);
  const canBuy = TOOL_PAYMENT_ENABLED && isAuthenticated && plans.length > 0;
  const go = (to, opts) => {
    onNavigate?.();
    navigate(to, opts);
  };

  let subTitle;
  const priceNote = plans[0] ? ` Giá ${formatVnd(plans[0].price)}, dùng ${plans[0].durationMonths || 12} tháng.` : '';
  if (!isAuthenticated) {
    subTitle = `Công cụ này cần mua để sử dụng.${priceNote} Vui lòng đăng nhập để mua hoặc kiểm tra công cụ đã mua.`;
  } else if (canBuy) {
    subTitle = `Công cụ này cần mua để sử dụng.${priceNote} Thanh toán bằng QR chuyển khoản, công cụ mở ngay sau khi chuyển khoản thành công.`;
  } else {
    subTitle = `Công cụ này cần mua để sử dụng.${priceNote} Anh/chị nhắn Zalo ${SUPPORT_HOTLINE_DISPLAY} để được hỗ trợ mua.`;
  }

  const actions = [];
  if (!isAuthenticated) {
    actions.push(
      <Button
        key="login"
        type="primary"
        style={{ background: '#005537', borderColor: '#005537' }}
        onClick={() => go('/login', { state: { from: location } })}
      >
        Đăng nhập
      </Button>,
      <Button key="register" onClick={() => go('/register')}>
        Tạo tài khoản
      </Button>
    );
  } else if (canBuy) {
    plans.forEach((p, i) =>
      actions.push(
        <Button
          key={p.id}
          type={i === 0 ? 'primary' : 'default'}
          icon={i === 0 ? <ShoppingCartOutlined /> : undefined}
          style={i === 0 ? { background: '#005537', borderColor: '#005537' } : undefined}
          onClick={() => go(`/checkout/${p.id}?returnTo=${returnTo}`)}
        >
          Mua công cụ · {formatVnd(p.price)}
          {p.durationMonths ? ` / ${p.durationMonths} tháng` : ''}
        </Button>
      )
    );
  } else {
    actions.push(
      <Button
        key="zalo"
        type="primary"
        href={SUPPORT_ZALO_URL}
        target="_blank"
        rel="noopener noreferrer"
        style={{ background: '#0068ff', borderColor: '#0068ff' }}
      >
        Nhắn Zalo tư vấn
      </Button>
    );
  }

  return { subTitle, actions };
}

/**
 * Modal mời mua công cụ: mở khi khách đang xem thử (chưa mua) bấm vào phần làm chi tiết
 * (nhập số liệu riêng, tải Excel, sao chép, in...).
 */
export function ToolPurchaseModal({ open, onClose, plans }) {
  const { subTitle, actions } = useToolPurchaseOptions(plans, onClose);
  return (
    <Modal open={open} onCancel={onClose} footer={null} centered width={520}>
      <Result
        icon={<LockOutlined style={{ color: '#f59e0b' }} />}
        title="Mua công cụ để làm chi tiết cho quầy của bạn"
        subTitle={`Bạn đang xem thử với số liệu mẫu. ${subTitle}`}
        extra={
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
            {actions}
            <Button key="close" onClick={onClose}>
              Tiếp tục xem thử
            </Button>
          </div>
        }
      />
    </Modal>
  );
}

/** Thanh báo đang xem thử đặt đầu trang công cụ, kèm nút mở modal mua */
export function ToolPreviewBanner({ onBuy, children }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
        background: '#fffbeb',
        border: '1px solid #fde68a',
        borderRadius: 12,
        padding: '12px 16px',
        margin: '16px 0',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, color: '#92400e', fontSize: 14, lineHeight: 1.55, flex: '1 1 320px' }}>
        <EyeOutlined style={{ fontSize: 18, marginTop: 2 }} />
        <span>{children}</span>
      </div>
      <Button
        type="primary"
        icon={<ShoppingCartOutlined />}
        onClick={onBuy}
        style={{ background: '#005537', borderColor: '#005537', fontWeight: 700, borderRadius: 8 }}
      >
        Mua công cụ
      </Button>
    </div>
  );
}
